import { useState, useEffect, useMemo } from "react";
import {
    Snackbar, TextField, Button, Typography, Box, Paper, Grid, Alert, Dialog,
    DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TableSortLabel,
    Toolbar, IconButton, Tooltip, CircularProgress, InputAdornment, Chip, MenuItem
} from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { visuallyHidden } from "@mui/utils";
import api from '../../api';
import { useDebounce } from "../../hooks/useDebounce";

const API_BASE = "/odomate/v1";

const getDealer = (row) => row.Dealership || row.dealership || {};

const getName = (row) => row.name || "";

const getStatus = (row) => {
    if (row.status) return row.status;
    const locked = row.accountLocked === true || row.account_locked === true;
    const expired = row.accountExpired === true || row.account_expired === true;
    return locked || expired ? "Inactive" : "Active";
};

const getDesignation = (row) => row.designation || "N/A";

const getContactNumber = (row) => row.contactNumber || row.contact_number || row.phoneNumber || row.phone_number || "N/A";

function descendingComparator(a, b, orderBy) {
    const getValue = (row) => {
        const dealer = getDealer(row);
        switch (orderBy) {
            case "name":
                return getName(row);
            case "designation":
                return getDesignation(row);
            case "dealerName":
                return dealer.dealerName || dealer.dealer_name || "";
            case "dealerCode":
                return dealer.dealerCode || dealer.dealer_code || "";
            case "status":
                return getStatus(row);
            case "contactNumber":
                return getContactNumber(row);
            default:
                return row[orderBy] || "";
        }
    };

    let valA = getValue(a);
    let valB = getValue(b);

    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
    { id: "name", label: "Name" },
    { id: "designation", label: "Designation" },
    { id: "dealerName", label: "Dealer Name" },
    { id: "dealerCode", label: "Dealer Code" },
    { id: "email", label: "Email" },
    { id: "status", label: "Status" },
    { id: "contactNumber", label: "Contact Number" },
    { id: "actions", label: "Actions" },
];

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
    const createSortHandler = (property) => (event) => onRequestSort(event, property);

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        {headCell.id === "actions" ? (
                            headCell.label
                        ) : (
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : "asc"}
                                onClick={createSortHandler(headCell.id)}
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        )}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

function EnhancedTableToolbar({ searchQuery, setSearchQuery, handleAddUserClick }) {
    return (
        <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, pt: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ flexShrink: 0 }} variant="h6" id="tableTitle" component="div">ODOMATE USERS</Typography>

                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search Users..."
                    value={searchQuery || ""}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ ml: 'auto', width: { xs: '100%', sm: 300 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                    }}
                />

                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddUserClick} sx={{ whiteSpace: 'nowrap' }}>
                    Add User
                </Button>
            </Box>
        </Toolbar>
    );
}

const UserForm = ({ userData, dealers, handleChange, onSubmit, handleClose, submitLabel = "Register" }) => (
    <Box component="form" noValidate sx={{ mt: 1 }}>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField name="name" required fullWidth label="Name" autoFocus value={userData.name} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="designation" fullWidth label="Designation" value={userData.designation} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="dealer_id" select required fullWidth label="Dealer" value={userData.dealer_id} onChange={handleChange}>
                    {dealers.map((dealer) => (
                        <MenuItem key={dealer.id} value={dealer.id}>
                            {(dealer.dealerName || dealer.dealer_name || "Unnamed Dealer")} ({dealer.dealerCode || dealer.dealer_code || "N/A"})
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid item xs={12}>
                <TextField name="email" required fullWidth label="Email Address" type="email" value={userData.email} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
                <TextField name="status" select required fullWidth label="Status" value={userData.status} onChange={handleChange}>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12}>
                <TextField name="contact_number" fullWidth label="Contact Number" value={userData.contact_number} onChange={handleChange} />
            </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, mb: 1 }}>
            <Button onClick={handleClose} color="inherit">Cancel</Button>
            <Button onClick={onSubmit} variant="contained" color="primary">{submitLabel}</Button>
        </Box>
    </Box>
);

export default function OdoMateUsers() {
    const [rows, setRows] = useState([]);
    const [dealers, setDealers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("name");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [errorMessage, setErrorMessage] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [openAddUser, setOpenAddUser] = useState(false);
    const [openEditUser, setOpenEditUser] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteEmail, setDeleteEmail] = useState("");
    const [editEmail, setEditEmail] = useState("");

    const [userData, setUserData] = useState({
        name: '', designation: '', dealer_id: '', email: '', status: 'Active', contact_number: '',
    });
    const [editUserData, setEditUserData] = useState({
        name: '', designation: '', dealer_id: '', email: '', status: 'Active', contact_number: '',
    });

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem("accessToken");
            const response = await api.get(`${API_BASE}/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRows(response.data);
            setErrorMessage(null);
        } catch (error) {
            console.error(error);
            setErrorMessage(error.response?.data?.message || "Error fetching data");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDealers = async () => {
        try {
            const token = localStorage.getItem("accessToken");
            const response = await api.get(`${API_BASE}/dealers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDealers(response.data);
        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: error.response?.data?.message || "Error fetching dealers", severity: "error" });
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchDealers();
    }, []);

    const filteredRows = useMemo(() => {
        if (!debouncedSearchQuery) return rows;
        const lowerQuery = debouncedSearchQuery.toLowerCase();

        return rows.filter((row) => {
            const dealer = getDealer(row);
            const searchValues = [
                getName(row),
                getDesignation(row),
                dealer.dealerName || dealer.dealer_name,
                dealer.dealerCode || dealer.dealer_code,
                row.email,
                getStatus(row),
                getContactNumber(row),
            ];

            return searchValues.some((value) => (value?.toString().toLowerCase() || "").includes(lowerQuery));
        });
    }, [rows, debouncedSearchQuery]);

    useEffect(() => { setPage(0); }, [debouncedSearchQuery]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

    const handleFormChange = (e, formType) => {
        const { name, value } = e.target;
        if (formType === 'user') setUserData((prev) => ({ ...prev, [name]: value }));
        else if (formType === 'editUser') setEditUserData((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditUserClick = (row) => {
        const dealer = getDealer(row);
        setEditEmail(row.email);
        setEditUserData({
            name: row.name || '',
            designation: row.designation || '',
            dealer_id: row.dealer_id || dealer.id || '',
            email: row.email || '',
            status: getStatus(row),
            contact_number: row.contactNumber || row.contact_number || '',
        });
        setOpenEditUser(true);
    };

    const handleUserUpdateClick = async () => {
        if (!editUserData.name || !editUserData.email || !editUserData.dealer_id || !editUserData.status) {
            setSnackbar({ open: true, message: "Please fill all required fields.", severity: "warning" });
            return;
        }
        const token = localStorage.getItem("accessToken");
        try {
            await api.put(`${API_BASE}/users/${editEmail}`, editUserData, { headers: { Authorization: `Bearer ${token}` } });
            setSnackbar({ open: true, message: "User updated successfully!", severity: "success" });
            setOpenEditUser(false);
            fetchUsers();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.errors || err.response?.data?.message || "Failed to update user.", severity: "error" });
        }
    };

    const handleUserCreateClick = async () => {
        if (!userData.name || !userData.email || !userData.dealer_id || !userData.status) {
            setSnackbar({ open: true, message: "Please fill all required fields.", severity: "warning" });
            return;
        }
        const token = localStorage.getItem("accessToken");
        try {
            await api.post(`${API_BASE}/users/register`, userData, { headers: { Authorization: `Bearer ${token}` } });
            setSnackbar({ open: true, message: "User created successfully!", severity: "success" });
            setUserData({ name: '', designation: '', dealer_id: '', email: '', status: 'Active', contact_number: '' });
            setOpenAddUser(false);
            fetchUsers();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.errors || err.response?.data?.message || "Failed to create user.", severity: "error" });
        }
    };

    const handleUserDeleteClick = async () => {
        const token = localStorage.getItem("accessToken");
        try {
            await api.delete(`${API_BASE}/users/${deleteEmail}`, { headers: { Authorization: `Bearer ${token}` } });
            setSnackbar({ open: true, message: "User deleted successfully!", severity: "success" });
            setOpenDeleteDialog(false);
            fetchUsers();
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.errors || err.response?.data?.message || "Failed to delete user.", severity: "error" });
            setOpenDeleteDialog(false);
        }
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;
    const visibleRows = useMemo(() => [...filteredRows].sort(getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [order, orderBy, page, rowsPerPage, filteredRows]);

    return (
        <Box sx={{ width: "100%" }}>
            <Paper sx={{ width: "100%", mb: 2, mt: 2 }}>
                <EnhancedTableToolbar
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    handleAddUserClick={() => setOpenAddUser(true)}
                />

                <TableContainer>
                    <Table sx={{ minWidth: 1100 }} aria-labelledby="tableTitle" size="medium">
                        <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : visibleRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Alert severity="info">{errorMessage || "No data available."}</Alert>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visibleRows.map((row, index) => {
                                    const dealer = getDealer(row);
                                    const status = getStatus(row);

                                    return (
                                        <TableRow hover tabIndex={-1} key={row.id || row.email || index}>
                                            <TableCell>{getName(row) || "N/A"}</TableCell>
                                            <TableCell>{getDesignation(row)}</TableCell>
                                            <TableCell>{dealer.dealerName || dealer.dealer_name || "N/A"}</TableCell>
                                            <TableCell>{dealer.dealerCode || dealer.dealer_code || "N/A"}</TableCell>
                                            <TableCell>{row.email || "N/A"}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={status}
                                                    size="small"
                                                    color={status.toLowerCase() === "active" ? "success" : "default"}
                                                    variant={status.toLowerCase() === "active" ? "filled" : "outlined"}
                                                />
                                            </TableCell>
                                            <TableCell>{getContactNumber(row)}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Edit User">
                                                    <IconButton
                                                        onClick={(e) => { e.stopPropagation(); handleEditUserClick(row); }}
                                                        size="small" color="primary"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete User">
                                                    <IconButton
                                                        onClick={(e) => { e.stopPropagation(); setDeleteEmail(row.email); setOpenDeleteDialog(true); }}
                                                        size="small" color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={8} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog open={openAddUser} onClose={() => setOpenAddUser(false)} fullWidth maxWidth="sm">
                <DialogTitle>Register User</DialogTitle>
                <DialogContent dividers>
                    <UserForm
                        userData={userData}
                        dealers={dealers}
                        handleChange={(e) => handleFormChange(e, 'user')}
                        onSubmit={handleUserCreateClick}
                        handleClose={() => setOpenAddUser(false)}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={openEditUser} onClose={() => setOpenEditUser(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit User</DialogTitle>
                <DialogContent dividers>
                    <UserForm
                        userData={editUserData}
                        dealers={dealers}
                        handleChange={(e) => handleFormChange(e, 'editUser')}
                        onSubmit={handleUserUpdateClick}
                        handleClose={() => setOpenEditUser(false)}
                        submitLabel="Save Changes"
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete user with email "{deleteEmail}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleUserDeleteClick} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
